// The "what stands out" sentences of the report, as data: a `type`, a `tone` and the numbers it
// needs. The view turns each one into a translated sentence.

import { getReadinessTotal } from "./baseline";
import {
  DAYS_PER_MONTH,
  LATEST_LOWEST_MIN_MONTHS,
  MAX_INSIGHTS,
  QUESTION_DROP_THRESHOLD,
  RPE_GAP_MIN,
} from "./constants";
import { mean, round1 } from "./math";

// "Session RPE on below-normal days vs the other judged days"; null unless both groups exist and
// the gap is big enough to say.
const rpeSplit = (questionnaireDays) => {
  const rpeOf = (days) =>
    days.map((d) => d.sessionRpe).filter((value) => value != null);
  const belowRpe = rpeOf(questionnaireDays.filter((d) => d.status === "below"));
  const otherRpe = rpeOf(
    questionnaireDays.filter(
      (d) => d.status === "within" || d.status === "above"
    )
  );
  if (belowRpe.length === 0 || otherRpe.length === 0) return null;
  const avgBelow = mean(belowRpe);
  const avgOther = mean(otherRpe);
  return avgBelow - avgOther >= RPE_GAP_MIN
    ? { avgBelow: round1(avgBelow), avgOther: round1(avgOther) }
    : null;
};

// The latest questionnaire is the lowest score the client ever had, with enough history to say so.
export const lowestEverMonths = ({ latestDay, firstDay, entries, toDay }) => {
  const latest = entries.get(latestDay);
  if (!latest) return null;
  const latestTotal = getReadinessTotal(latest.readiness);
  for (let day = firstDay; day <= toDay; day++) {
    const entry = entries.get(day);
    if (
      entry &&
      day !== latestDay &&
      getReadinessTotal(entry.readiness) <= latestTotal
    ) {
      return null;
    }
  }
  const months = Math.round((latestDay - firstDay) / DAYS_PER_MONTH);
  return months >= LATEST_LOWEST_MIN_MONTHS ? months : null;
};
/**
 * Finds the biggest question drop in the questions.
 * @param {*} questions
 * @returns
 */
const biggestQuestionDrop = (questions) => {
  const worst = questions
    .filter((q) => q.change != null && q.change <= -QUESTION_DROP_THRESHOLD)
    .sort((a, b) => a.change - b.change)[0];
  return worst ?? null;
};

// The training days the client did not answer for. Also the one insight a period without any
// questionnaire can have.
export const missedDaysInsight = (summary) =>
  summary.missedDays.length > 0
    ? {
        type: "missedDays",
        tone: "warning",
        params: {
          completed: summary.completedTrainingDays,
          trainingDays: summary.trainingDays,
          missed: summary.missedDays,
        },
      }
    : null;

// Insights as data, most important first, at most MAX_INSIGHTS. Each one only exists when its
// condition is true. `tone` is "warning" (needs attention) or "info".
export const buildInsights = ({
  state,
  normal,
  summary,
  questions,
  questionnaireDays,
  latest,
  lowestMonths,
}) => {
  const insights = [];
  if (state === "new") {
    insights.push({
      type: "learning",
      tone: "info",
      params: { checkIns: normal.checkIns },
    });
  }
  if (summary.judged > 0 && summary.below > 0) {
    insights.push({
      type: "belowNormal",
      tone: "warning",
      params: {
        below: summary.below,
        judged: summary.judged,
        lo: normal.total?.lo ?? null,
        hi: normal.total?.hi ?? null,
      },
    });
  }
  const drop = biggestQuestionDrop(questions);
  if (drop) {
    insights.push({
      type: "questionDrove",
      tone: "warning",
      params: { question: drop.key, avg: drop.avg, prevAvg: drop.prevAvg },
    });
  }
  const missed = missedDaysInsight(summary);
  if (missed) insights.push(missed);
  const rpe = rpeSplit(questionnaireDays);
  if (rpe) insights.push({ type: "rpeOnBelowDays", tone: "info", params: rpe });
  if (lowestMonths != null) {
    insights.push({
      type: "latestLowest",
      tone: "warning",
      params: {
        utcDate: latest.utcDate,
        total: latest.total,
        months: lowestMonths,
      },
    });
  }
  return insights.slice(0, MAX_INSIGHTS);
};
